/**
 * Event Handling Module
 * Contains PageTracker, LoadingDetector, DOMStabilityChecker, NavigationCompleteDetector, and BatchedActionExecutor
 */

import { isSDKWidget, getSanitizedUrl, HybridTarget, BatchStep, BatchedActionRequest, humanLikeClick } from './types';
import { DOMIntrospector } from './dom-capture';
import { OverlaySystem } from './ui-rendering';
import { HybridTargetResolver, ActionExecutor } from './action-execution';
import { logger as defaultLogger, Logger } from './logger';

/**
 * Interface for the SDK dependency used by PageTracker.
 * This avoids circular imports by not depending on the OnboardingSDK class directly.
 */
export interface PageTrackerSDK {
  sendMetadata(data: Record<string, any>): void;
  _botActionDepth: number;
  fastExecutionMode: boolean;
}

/**
 * PageTracker - Tracks user interactions and sends them as metadata
 * Now integrated with DOMIntrospector for immediate DOM capture after actions.
 * Tracks focus/blur and keyboard events for React/SPA form interactions.
 *
 * ## What the server actually consumes (FLO-456)
 *
 * The interaction listeners do two separate jobs: they send a metadata message,
 * and they trigger an immediate DOM capture. Only some of the messages are read.
 * Verified against `bot_refactored.py`'s `on_rtvi_client_message`:
 *
 *   click       → recorded AND injected as Shepherd context. KEEP.
 *   form_input  → recorded via record_ui_event. KEEP.
 *   focus/blur  → routed to record_ui_event, which has no branch for them and
 *                 no `else` — a silent no-op. DROPPED.
 *   keydown     → falls through to `[RTVI] Unhandled message type`. DROPPED.
 *   scroll      → same. DROPPED.
 *
 * The machinery that once consumed focus/blur/keydown —
 * `OptimizedMetadataProcessor.POTENTIAL_UI_CHANGE_EVENTS` — is dead in the
 * two-pipeline architecture: `metadata_task` is constructed and queued into,
 * but the runner only ever runs `main_task` and `dom_context_task`.
 *
 * So those four listeners now exist ONLY to trigger a capture (`scheduleCapture`).
 * That capture is the real signal — a dropdown that opened on focus, validation
 * text that appeared on blur, the result of an Enter keypress, content revealed
 * by a scroll — and it arrives as `dom_snapshot` / `accessible_snapshot`, which
 * the server does read.
 *
 * If you re-add one of these messages, add its consumer in the same change.
 */
export class PageTracker {
  private readonly logger: Logger;
  private sdk: PageTrackerSDK;
  private domIntrospector: DOMIntrospector | null = null;
  private lastUrl: string = '';
  private clickHandler: ((e: MouseEvent) => void) | null = null;
  private focusHandler: ((e: FocusEvent) => void) | null = null;
  private blurHandler: ((e: FocusEvent) => void) | null = null;
  private inputHandler: ((e: Event) => void) | null = null;
  private keydownHandler: ((e: KeyboardEvent) => void) | null = null;
  private inputDebounceTimers: Map<Element, ReturnType<typeof setTimeout>> = new Map();
  private isTracking: boolean = false;

  /**
   * Every in-flight "capture shortly after this event" timer.
   *
   * The interaction listeners all delay their capture 100-500ms to let the host
   * framework re-render. Those handles were untracked, so `stop()` removed the
   * listeners but left up to half a second of pending callbacks armed — each of
   * which would call into a DOMIntrospector that had already been shut down, and
   * `stopCapturing()` does not clear `sendCallback`, so a full whole-document
   * walk could still run and be sent after teardown.
   */
  private captureTimers: Set<ReturnType<typeof setTimeout>> = new Set();

  // Scroll tracking for Fast Execution System
  private scrollHandler: (() => void) | null = null;
  private scrollDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  // URL tracking for SPAs
  private urlCheckInterval: ReturnType<typeof setInterval> | null = null;
  private popstateHandler: (() => void) | null = null;
  private popstateTimer: ReturnType<typeof setTimeout> | null = null;

  // Callback fired after navigation detected (for fresh snapshot capture)
  private onNavigationCallback: ((url: string) => void) | null = null;

  // CDP Phase 1 (FLO-547): visitor-analytics page-view hook, fired with the
  // current pathname from the SAME poll/popstate detection that produces
  // page_metadata — deliberately NOT a second poller. The subscriber dedupes
  // consecutive same-path reports.
  private onPageViewCallback: ((path: string) => void) | null = null;

  /**
   * Ask for a capture after `delayMs`, with the handle tracked so `stop()` can
   * cancel it. Use this for every delayed capture — a bare setTimeout here
   * outlives teardown.
   *
   * The `isTracking` check is not redundant with `stop()` clearing the set: a
   * timer that is already RUNNING can arm a new one. `popstateHandler`'s deferred
   * body calls `sendPageMetadata()`, which schedules a capture — so a popstate
   * landing just before teardown could arm a fresh 500ms capture *after* stop()
   * had emptied the set. This refuses that, whatever the path.
   */
  private scheduleDelayedCapture(reason: string, delayMs: number, then?: () => void) {
    if (!this.isTracking) return;
    const handle = setTimeout(() => {
      this.captureTimers.delete(handle);
      if (!this.isTracking) return;
      this.domIntrospector?.scheduleCapture(reason);
      then?.();
    }, delayMs);
    this.captureTimers.add(handle);
  }

  constructor(sdk: PageTrackerSDK, logger: Logger = defaultLogger) {
    this.logger = logger;
    this.sdk = sdk;
    this.lastUrl = window.location.href;
  }

  /**
   * Set the DOM introspector for immediate capture triggers
   */
  setDOMIntrospector(introspector: DOMIntrospector) {
    this.domIntrospector = introspector;
    this.logger.log('[PageTracker] DOMIntrospector set for sync captures');
  }

  /**
   * Set a callback to fire after navigation is detected.
   * Used by the SDK to trigger fresh accessible snapshot captures post-navigation.
   */
  onNavigation(callback: (url: string) => void) {
    this.onNavigationCallback = callback;
  }

  /**
   * Set the visitor-analytics page-view hook (CDP Phase 1, FLO-547). Fires on
   * tracking start and on every detected URL change, alongside page_metadata.
   */
  onPageView(callback: (path: string) => void) {
    this.onPageViewCallback = callback;
  }

  /**
   * Start tracking page events.
   *
   * Two independently-installable halves (FLO-451):
   *
   *   page context  — always on. `page_metadata` at start and on every URL
   *                   change, plus the post-navigation hook. One setInterval
   *                   and one message per navigation; every agent wants to
   *                   know which page the visitor is on (see FLO-362's
   *                   page-scoped nudges).
   *   interactions  — `options.interactions`, default true. The click /
   *                   scroll / focus / blur / input / keydown listeners that
   *                   stream `sendMetadata` per event AND drive the
   *                   DOMIntrospector's immediate captures. Only the
   *                   onboarding agent acts on the host page, so only it
   *                   should pay for these — the server drops them for the
   *                   demo and website agents.
   *
   * @param options.interactions Install the interaction listeners (default true)
   */
  start(options: { interactions?: boolean } = {}) {
    if (this.isTracking) return;
    const trackInteractions = options.interactions !== false;
    this.isTracking = true;
    this.logger.log(
      `[PageTracker] Starting page event tracking (interactions: ${trackInteractions ? 'on' : 'off'})`
    );

    // Send initial page context
    this.sendPageMetadata();

    // Track URL changes (SPA navigation)
    this.setupUrlTracking();

    if (!trackInteractions) {
      // Page context only. No listeners on the host page, so no per-event
      // metadata and no querySelectorAll('*') / getBoundingClientRect sweeps
      // wired to scroll — the performance tax this mode exists to avoid.
      this.logger.log('[PageTracker] Interaction listeners skipped (agent does not drive the host page)');
      return;
    }

    // Track clicks
    this.clickHandler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target) return;

      // Skip our own SDK widget elements
      if (isSDKWidget(target)) return;

      // Get rich element metadata for better replay targeting
      const metadata = this.getElementMetadata(target);

      // Determine event source: bot action vs user action
      // This allows the server to distinguish between events and tag them appropriately
      const source = (this.sdk._botActionDepth > 0) ? 'bot' : 'user';

      this.sdk.sendMetadata({
        type: 'click',
        selector: metadata.selector,
        text: metadata.text,
        tagName: metadata.tagName,
        // Include rich targeting data for HybridTargetResolver
        role: metadata.role,
        ariaLabel: metadata.ariaLabel,
        placeholder: metadata.placeholder,
        labelText: metadata.labelText,
        timestamp: Date.now(),
        source: source  // 'bot' or 'user' - helps server format LLM context
      });

      this.logger.log(`[PageTracker] Click (${source}):`, metadata.selector, metadata.labelText ? `(label: ${metadata.labelText})` : '');

      // SYNC: Trigger immediate DOM capture after click
      // Small delay to allow DOM to update from the click action
      if (this.domIntrospector) {
        this.scheduleDelayedCapture('post-click', 100);
      }
    };
    document.addEventListener('click', this.clickHandler, true);

    // Track scroll events (debounced) - important for Fast Execution System
    // Captures DOM after user scrolls to reveal new content
    this.scrollHandler = () => {
      // Skip scroll tracking during fast execution mode (programmatic scrolling)
      if (this.sdk.fastExecutionMode) {
        return;
      }

      if (this.scrollDebounceTimer) {
        clearTimeout(this.scrollDebounceTimer);
      }

      this.scrollDebounceTimer = setTimeout(() => {
        // Double-check fast mode in case it changed during debounce
        if (this.sdk.fastExecutionMode) {
          return;
        }

        this.logger.log('[PageTracker] Scroll detected, capturing DOM');

        // FLO-456: no `scroll` metadata message. Nothing on the server reads it
        // (see the file header), and the post-scroll snapshot below already
        // carries the viewport plus every in-viewport element's rect — which is
        // what element targeting actually consumes.
        //
        // Trigger DOM capture after scroll settles
        if (this.domIntrospector) {
          this.domIntrospector.scheduleCapture('post-scroll');
        }
      }, 200);  // 200ms debounce
    };
    window.addEventListener('scroll', this.scrollHandler, { passive: true });

    // Track focus events on inputs
    // Important for React/SPA apps where focusing can trigger dropdowns, autocomplete, etc.
    this.focusHandler = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (!target || !this.isInteractiveElement(target)) return;

      // Skip SDK widget elements
      if (isSDKWidget(target)) return;

      // FLO-456: no `focus` metadata message, and therefore no
      // getElementMetadata() — that walks up to 5 ancestors and runs a
      // querySelector label lookup, per focus event, to build a payload the
      // server drops on the floor.
      this.logger.log('[PageTracker] Focus:', target.tagName.toLowerCase());

      // SYNC: Capture DOM when user focuses on input - autocomplete/dropdowns may appear
      if (this.domIntrospector && this.isInputElement(target)) {
        // Delay for dropdowns/autocomplete to appear
        this.scheduleDelayedCapture('post-focus', 150);
      }
    };
    document.addEventListener('focus', this.focusHandler, true);

    // Track blur events
    // Important for React/SPA apps where blur can trigger validation, state updates, etc.
    this.blurHandler = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (!target || !this.isInteractiveElement(target)) return;

      // Skip SDK widget elements
      if (isSDKWidget(target)) return;

      // FLO-456: no `blur` metadata message (and no getSelector() to build it).
      this.logger.log('[PageTracker] Blur:', target.tagName.toLowerCase());

      // SYNC: Capture DOM after blur - form validation, dropdowns closing, state changes
      if (this.domIntrospector && this.isInputElement(target)) {
        // Delay for validation messages, state updates
        this.scheduleDelayedCapture('post-blur', 200);
      }
    };
    document.addEventListener('blur', this.blurHandler, true);

    // Track form inputs (debounced)
    this.inputHandler = (e: Event) => {
      const target = e.target as HTMLInputElement | HTMLTextAreaElement;
      if (!target) return;

      // Skip SDK widget elements
      if (isSDKWidget(target)) return;

      // Determine event source: bot fill vs the user typing. Same rule as the
      // click handler above — and just as necessary, because this listener is on
      // `document` in the capture phase with no isTrusted check, so the synthetic
      // `new Event('input')` the executor dispatches after a FILL is
      // indistinguishable from a keystroke once it reaches the server.
      //
      // Sampled HERE, not inside the debounce: _botActionDepth is back to 0 by
      // the time a 500ms timer fires, which would tag every bot fill as 'user'.
      const source = (this.sdk._botActionDepth > 0) ? 'bot' : 'user';

      // Clear existing debounce timer for this element
      const existingTimer = this.inputDebounceTimers.get(target);
      if (existingTimer) clearTimeout(existingTimer);

      // Debounce input events to avoid flooding
      const timer = setTimeout(() => {
        const metadata = this.getElementMetadata(target);
        const fieldName = target.name || target.id || target.placeholder || metadata.labelText || 'field';

        this.sdk.sendMetadata({
          type: 'form_input',
          selector: metadata.selector,
          field: fieldName,
          inputType: target.type || 'text',
          // Don't send actual value for privacy
          hasValue: !!target.value,
          role: metadata.role,
          ariaLabel: metadata.ariaLabel,
          placeholder: metadata.placeholder,
          labelText: metadata.labelText,
          timestamp: Date.now(),
          source: source  // 'bot' or 'user' - see the click handler
        });

        this.logger.log(`[PageTracker] Form input (${source}):`, fieldName, metadata.labelText ? `(label: ${metadata.labelText})` : '');
        this.inputDebounceTimers.delete(target);

        // SYNC: Trigger immediate DOM capture after form input
        if (this.domIntrospector) {
          this.domIntrospector.scheduleCapture('post-input');
        }
      }, 500);

      this.inputDebounceTimers.set(target, timer);
    };
    document.addEventListener('input', this.inputHandler, true);

    // Track keyboard events (Enter key especially triggers actions in React apps)
    this.keydownHandler = (e: KeyboardEvent) => {
      // Defensive check: ensure target is an HTMLElement
      // (e.target can be Document if event is dispatched on document directly)
      const target = e.target;
      if (!target || !(target instanceof HTMLElement)) return;

      // Skip our own SDK widget elements
      if (isSDKWidget(target)) return;

      // Only track actionable keys on input elements
      if (!this.isInputElement(target)) return;

      const actionKeys = ['Enter', 'Escape', 'Tab'];
      if (!actionKeys.includes(e.key)) return;

      // FLO-456: no `keydown` metadata message (and no getSelector() to build
      // it). The snapshot below is the signal the server actually uses — an
      // Enter that submitted a form shows up as the resulting DOM change.
      this.logger.log('[PageTracker] Keydown:', e.key, 'on', target.tagName.toLowerCase());

      // SYNC: Enter key often triggers search, form submit, dropdown selection
      // Tab moves focus which can trigger UI changes
      // Escape often closes modals/dropdowns
      if (this.domIntrospector) {
        const delay = e.key === 'Enter' ? 300 : 150;  // Longer delay for Enter (API calls, etc)
        this.scheduleDelayedCapture(`post-keydown-${e.key.toLowerCase()}`, delay);
      }
    };
    document.addEventListener('keydown', this.keydownHandler, true);

    this.logger.log('[PageTracker] Event listeners attached');
  }

  /**
   * Stop tracking page events
   */
  stop() {
    if (!this.isTracking) return;
    this.isTracking = false;

    if (this.clickHandler) {
      document.removeEventListener('click', this.clickHandler, true);
      this.clickHandler = null;
    }
    if (this.focusHandler) {
      document.removeEventListener('focus', this.focusHandler, true);
      this.focusHandler = null;
    }
    if (this.blurHandler) {
      document.removeEventListener('blur', this.blurHandler, true);
      this.blurHandler = null;
    }
    if (this.inputHandler) {
      document.removeEventListener('input', this.inputHandler, true);
      this.inputHandler = null;
    }
    if (this.keydownHandler) {
      document.removeEventListener('keydown', this.keydownHandler, true);
      this.keydownHandler = null;
    }

    // Remove scroll handler
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
      this.scrollHandler = null;
    }
    if (this.scrollDebounceTimer) {
      clearTimeout(this.scrollDebounceTimer);
      this.scrollDebounceTimer = null;
    }

    // Clean up URL tracking
    if (this.urlCheckInterval) {
      clearInterval(this.urlCheckInterval);
      this.urlCheckInterval = null;
    }
    if (this.popstateHandler) {
      window.removeEventListener('popstate', this.popstateHandler);
      this.popstateHandler = null;
    }
    if (this.popstateTimer) {
      clearTimeout(this.popstateTimer);
      this.popstateTimer = null;
    }

    // Clear all debounce timers
    this.inputDebounceTimers.forEach(timer => { clearTimeout(timer); });
    this.inputDebounceTimers.clear();

    // Cancel pending post-event captures BEFORE the introspector is torn down.
    // Removing the listeners above does not retract callbacks already queued by
    // events that fired moments earlier.
    this.captureTimers.forEach(timer => { clearTimeout(timer); });
    this.captureTimers.clear();

    this.logger.log('[PageTracker] Stopped tracking');
  }

  /**
   * Setup URL change tracking for SPAs
   */
  private setupUrlTracking() {
    // Don't setup if already tracking
    if (this.urlCheckInterval) return;

    // Check for URL changes periodically (handles pushState/replaceState)
    this.urlCheckInterval = setInterval(() => {
      const currentUrl = window.location.href;
      if (currentUrl !== this.lastUrl) {
        this.lastUrl = currentUrl;
        this.sendPageMetadata();
      }
    }, 1000);

    // Also listen for popstate (back/forward buttons)
    this.popstateHandler = () => {
      // Use setTimeout to allow the browser to update location.href.
      // Tracked: this callback reaches sendPageMetadata, which schedules a
      // capture — so an untracked handle here can arm work after teardown.
      if (this.popstateTimer) clearTimeout(this.popstateTimer);
      this.popstateTimer = setTimeout(() => {
        this.popstateTimer = null;
        if (!this.isTracking) return;
        const currentUrl = window.location.href;
        // Only send metadata if URL actually changed (prevents duplicate events with urlCheckInterval)
        if (currentUrl !== this.lastUrl) {
          // Update lastUrl before sending to prevent race with urlCheckInterval
          this.lastUrl = currentUrl;
          this.sendPageMetadata();
        }
      }, 100);
    };
    window.addEventListener('popstate', this.popstateHandler);
  }

  /**
   * Send current page metadata
   */
  sendPageMetadata() {
    const url = getSanitizedUrl();

    this.sdk.sendMetadata({
      type: 'page_metadata',
      url,
      title: document.title,
      pathname: window.location.pathname,
      timestamp: Date.now()
    });

    // CDP Phase 1 (FLO-547): PAGE_VIEWED rides the same detection. Wrapped so
    // an analytics failure can never break page tracking.
    try {
      this.onPageViewCallback?.(window.location.pathname);
    } catch (e) {
      this.logger.warn('[PageTracker] onPageView callback threw:', e);
    }

    this.logger.log('[PageTracker] Page metadata sent:', url);

    // After navigation, wait for page render then send fresh snapshots
    // so the executor doesn't act on stale DOM from the previous page
    this.scheduleDelayedCapture('post-navigation', 500, () => {
      this.onNavigationCallback?.(url);
    });
  }

  /**
   * Check if element is interactive
   */
  private isInteractiveElement(el: HTMLElement): boolean {
    const tagName = el.tagName.toLowerCase();
    return ['input', 'textarea', 'select', 'button'].includes(tagName) ||
      el.contentEditable === 'true';
  }

  /**
   * Check if element is a text input (focus/blur can trigger UI changes)
   */
  private isInputElement(el: HTMLElement): boolean {
    const tagName = el.tagName.toLowerCase();
    if (tagName === 'textarea') return true;
    if (tagName === 'input') {
      const type = (el as HTMLInputElement).type || 'text';
      // Text-like inputs that can trigger search, autocomplete, etc.
      return ['text', 'search', 'email', 'password', 'tel', 'url', 'number'].includes(type);
    }
    if (el.contentEditable === 'true') return true;
    return false;
  }

  /**
   * Get unique selector for element - prioritizes stable, semantic selectors
   */
  private getSelector(element: Element): string {
    // Priority 1: ID (most stable)
    if (element.id && !element.id.match(/^(react-select-|:r|radix-)/)) {
      return `#${CSS.escape(element.id)}`;
    }

    // Priority 2: Stable semantic selectors
    const role = element.getAttribute('role');
    const ariaLabel = element.getAttribute('aria-label');
    const name = element.getAttribute('name');
    const dataTestId = element.getAttribute('data-testid') || element.getAttribute('data-test-id');
    const placeholder = (element as HTMLInputElement).placeholder;

    // Build selector from most stable attributes
    const tag = element.tagName.toLowerCase();
    let selector = tag;

    // data-testid is most stable (explicitly for testing)
    if (dataTestId) {
      return `[data-testid="${dataTestId}"]`;
    }

    // Role-based selector (ARIA roles are semantic and stable)
    if (role) {
      selector = `${tag}[role="${role}"]`;
      // If there might be multiple, add distinguishing attribute
      if (ariaLabel) {
        selector += `[aria-label="${ariaLabel}"]`;
      } else if (name) {
        selector += `[name="${name}"]`;
      }
      return selector;
    }

    // Name attribute (common for form inputs)
    if (name) {
      return `${tag}[name="${name}"]`;
    }

    // Placeholder (useful for inputs)
    if (placeholder) {
      return `${tag}[placeholder="${placeholder}"]`;
    }

    // aria-label
    if (ariaLabel) {
      return `${tag}[aria-label="${ariaLabel}"]`;
    }

    // Priority 3: Build a path but prefer stable classes
    const path: string[] = [];
    let current: Element | null = element;
    let depth = 0;

    while (current && current !== document.body && depth < 5) {
      let partSelector = current.tagName.toLowerCase();

      // Check for stable attributes on this element
      const currentRole = current.getAttribute('role');
      const currentTestId = current.getAttribute('data-testid');

      if (currentTestId) {
        path.unshift(`[data-testid="${currentTestId}"]`);
        break; // Found a stable anchor
      }

      if (currentRole) {
        partSelector += `[role="${currentRole}"]`;
      } else if (current.className && typeof current.className === 'string') {
        // Filter out generated/volatile classes
        const classes = current.className.split(' ')
          .filter(c => c &&
            !c.startsWith('css-') &&      // Emotion/styled-components
            !c.startsWith('sc-') &&       // styled-components
            !c.startsWith('ng-') &&       // Angular
            !c.startsWith('_') &&         // Private
            !c.match(/^[a-z]{6,}$/) &&    // Random hashes
            !c.match(/^\d/)               // Starts with number
          )
          .slice(0, 2);

        if (classes.length > 0) {
          partSelector += `.${classes.join('.')}`;
        }
      }

      path.unshift(partSelector);
      current = current.parentElement;
      depth++;
    }

    return path.join(' > ');
  }

  /**
   * Get rich element metadata for HybridTarget resolution
   * This provides all the information needed for multi-strategy element finding
   */
  private getElementMetadata(element: Element): {
    selector: string;
    text: string;
    tagName: string;
    role?: string;
    ariaLabel?: string;
    placeholder?: string;
    labelText?: string;
  } {
    const htmlEl = element as HTMLElement;
    const selector = this.getSelector(element);
    const text = htmlEl.innerText?.trim().substring(0, 100) || '';
    const tagName = element.tagName.toLowerCase();

    // Gather ARIA/semantic attributes
    const role = element.getAttribute('role') || undefined;
    const ariaLabel = element.getAttribute('aria-label') || undefined;
    const placeholder = (element as HTMLInputElement).placeholder || undefined;

    // Try to find associated label
    let labelText: string | undefined;

    // Check aria-labelledby
    const ariaLabelledBy = element.getAttribute('aria-labelledby');
    if (ariaLabelledBy) {
      const labelEl = document.getElementById(ariaLabelledBy.split(/\s+/)[0]);
      if (labelEl) {
        labelText = labelEl.textContent?.trim();
      }
    }

    // Check for label[for] association
    const inputId = element.getAttribute('id');
    if (!labelText && inputId) {
      try {
        const labelFor = document.querySelector(`label[for="${CSS.escape(inputId)}"]`);
        if (labelFor) {
          labelText = labelFor.textContent?.trim().replace(/\*$/, '').trim();
        }
      } catch (e) {
        // Invalid ID for CSS selector, skip
      }
    }

    // Check parent container for label (use specific container classes, not generic 'div')
    if (!labelText) {
      const container = element.closest('.MuiFormControl-root, fieldset, [class*="form-group"], [class*="form-field"], [class*="field-wrapper"]');
      if (container) {
        // Look for direct child label only (not nested in other form fields)
        const label = container.querySelector(':scope > label, :scope > .MuiFormLabel-root, :scope > div > label');
        if (label) {
          // Make sure this label is visually associated with this element
          const inputs = container.querySelectorAll('input, select, textarea, [role="combobox"]');
          // Check if there's only one input OR if our element contains one of the inputs
          const elementContainsInput = Array.from(inputs).some(input => element.contains(input) || element === input);
          if (inputs.length === 1 || elementContainsInput) {
            labelText = label.textContent?.trim().replace(/\*$/, '').trim();
          }
        }
      }
    }

    return {
      selector,
      text,
      tagName,
      role,
      ariaLabel,
      placeholder,
      labelText
    };
  }
}

// ============================================================================
// FAST EXECUTION SYSTEM - Phase 1 & 2 Implementation
// ============================================================================

/**
 * LoadingDetector - Detects loading indicators on the page
 * Used to wait for page to be ready before proceeding
 */
export class LoadingDetector {
  private readonly logger: Logger;

  constructor(logger: Logger = defaultLogger) {
    this.logger = logger;
  }

  private loadingSelectors = [
    '[role="progressbar"]',
    '.loading', '.spinner', '.loader',
    '[aria-busy="true"]',
    '[class*="loading"]', '[class*="spinner"]',
    'svg[class*="animate-spin"]',
    '[data-loading="true"]',
    '.MuiCircularProgress-root',
    '.MuiLinearProgress-root',
  ];

  /** Track last detected selector to avoid flooding logs on every 50ms poll */
  private _lastLoggedSelector: string | null = null;

  /**
   * Check if page is currently showing loading indicators
   */
  isLoading(): boolean {
    for (const selector of this.loadingSelectors) {
      try {
        const el = document.querySelector(selector);
        if (el && this.isVisible(el as HTMLElement)) {
          // Only log on first detection or selector change (not every poll)
          if (this._lastLoggedSelector !== selector) {
            this.logger.log('[LoadingDetector] Found loading indicator:', selector);
            this._lastLoggedSelector = selector;
          }
          return true;
        }
      } catch (e) {
        // Ignore invalid selector errors
      }
    }
    this._lastLoggedSelector = null;
    return false;
  }

  /**
   * Wait for loading indicators to disappear
   * @param timeoutMs Maximum time to wait
   * @returns true if loading completed, false if timed out
   */
  async waitForLoadingComplete(timeoutMs = 5000): Promise<boolean> {
    const start = Date.now();
    this.logger.log('[LoadingDetector] Waiting for loading to complete...');

    while (Date.now() - start < timeoutMs) {
      if (!this.isLoading()) {
        // Wait a bit more to ensure stability
        await new Promise(r => setTimeout(r, 100));
        if (!this.isLoading()) {
          this.logger.log('[LoadingDetector] Loading complete');
          return true;
        }
      }
      await new Promise(r => setTimeout(r, 50));
    }

    this.logger.warn('[LoadingDetector] Timed out waiting for loading');
    return false; // Timed out
  }

  private isVisible(el: HTMLElement): boolean {
    const style = getComputedStyle(el);
    // Check basic visibility properties
    if (style.visibility === 'hidden' || style.display === 'none' || style.opacity === '0') {
      return false;
    }
    // offsetParent is null for hidden elements, but also for fixed/sticky positioned elements
    // So we need to check position explicitly for those cases
    if (el.offsetParent === null) {
      const position = style.position;
      if (position !== 'fixed' && position !== 'sticky') {
        return false;
      }
    }
    // Also check if element has dimensions
    const rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }
}

/**
 * DOMStabilityChecker - Waits for DOM to settle (no mutations)
 * Useful for detecting when SPA navigation has completed
 */
export class DOMStabilityChecker {
  private readonly logger: Logger;

  constructor(logger: Logger = defaultLogger) {
    this.logger = logger;
  }

  /**
   * Wait for DOM to be stable (no mutations for specified duration)
   * @param stableMs Duration with no mutations to consider stable
   * @param maxWaitMs Maximum total time to wait (prevents infinite waiting)
   */
  async waitForStable(stableMs: number = 300, maxWaitMs: number = 5000): Promise<void> {
    return new Promise((resolve) => {
      let timeoutId: ReturnType<typeof setTimeout>;
      let maxTimeoutId: ReturnType<typeof setTimeout>;

      const cleanup = () => {
        clearTimeout(timeoutId);
        clearTimeout(maxTimeoutId);
        observer.disconnect();
      };

      const observer = new MutationObserver(() => {
        // Reset timer on any mutation
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          cleanup();
          this.logger.log('[DOMStabilityChecker] DOM stable');
          resolve();
        }, stableMs);
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
      });

      // Initial timeout in case no mutations
      timeoutId = setTimeout(() => {
        cleanup();
        this.logger.log('[DOMStabilityChecker] DOM stable (no mutations)');
        resolve();
      }, stableMs);

      // Maximum wait timeout to prevent infinite waiting
      maxTimeoutId = setTimeout(() => {
        cleanup();
        this.logger.warn('[DOMStabilityChecker] Max wait time reached, proceeding anyway');
        resolve();
      }, maxWaitMs);
    });
  }
}

/**
 * NavigationCompleteDetector - Detects when SPA navigation has completed
 * Monitors URL changes, loading indicators, and DOM stability
 */
export class NavigationCompleteDetector {
  private readonly logger: Logger;
  private lastUrl: string = '';
  private urlChangeCallback: ((url: string) => void) | null = null;
  private loadingDetector: LoadingDetector;
  private domStabilityChecker: DOMStabilityChecker;
  private originalPushState: typeof history.pushState | null = null;
  private originalReplaceState: typeof history.replaceState | null = null;
  private popstateHandler: (() => void) | null = null;
  private isStarted: boolean = false;

  constructor(logger: Logger = defaultLogger) {
    this.logger = logger;
    this.lastUrl = window.location.href;
    this.loadingDetector = new LoadingDetector(logger);
    this.domStabilityChecker = new DOMStabilityChecker(logger);
  }

  /**
   * Start monitoring for navigation completion
   */
  start(onNavigationComplete: (url: string) => void) {
    // Prevent double-start - stop first if already started
    if (this.isStarted) {
      this.logger.warn('[NavigationCompleteDetector] Already started, stopping first');
      this.stop();
    }

    this.logger.log('[NavigationCompleteDetector] Starting navigation monitoring');
    this.isStarted = true;
    this.urlChangeCallback = onNavigationComplete;
    this.lastUrl = window.location.href;  // Reset last URL

    // Listen for URL changes (SPA navigation)
    this.setupUrlObserver();

    // Listen for popstate (browser back/forward)
    this.popstateHandler = () => this.handleUrlChange();
    window.addEventListener('popstate', this.popstateHandler);
  }

  private setupUrlObserver() {
    // Store original methods only if not already stored (prevents double-wrapping)
    if (!this.originalPushState) {
      this.originalPushState = history.pushState.bind(history);
    }
    if (!this.originalReplaceState) {
      this.originalReplaceState = history.replaceState.bind(history);
    }

    const self = this;

    history.pushState = function (...args: Parameters<typeof history.pushState>) {
      self.originalPushState!(...args);
      self.handleUrlChange();
    };

    history.replaceState = function (...args: Parameters<typeof history.replaceState>) {
      self.originalReplaceState!(...args);
      self.handleUrlChange();
    };
  }

  private handleUrlChange = async () => {
    const newUrl = window.location.href;
    if (newUrl === this.lastUrl) return;

    this.logger.log('[NavigationCompleteDetector] URL changed:', newUrl);
    this.lastUrl = newUrl;

    // Wait for page to stabilize (with timeout)
    try {
      await this.waitForPageStable();
    } catch (e) {
      this.logger.warn('[NavigationCompleteDetector] Error waiting for page stable:', e);
    }

    // Signal navigation complete with sanitized URL
    if (this.urlChangeCallback) {
      this.urlChangeCallback(getSanitizedUrl());
    }
  };

  /**
   * Wait for page to be stable after navigation
   * Has overall timeout to prevent hanging
   */
  async waitForPageStable(): Promise<void> {
    this.logger.log('[NavigationCompleteDetector] Waiting for page to stabilize...');

    const maxWaitTime = 8000;  // Overall max wait
    const startTime = Date.now();

    // Wait for loading indicators to disappear (max 5s)
    await this.loadingDetector.waitForLoadingComplete(5000);

    // Check if we still have time for DOM stability check
    const remaining = maxWaitTime - (Date.now() - startTime);
    if (remaining > 300) {
      // Wait for DOM to settle (no mutations for 300ms, max remaining time)
      await this.domStabilityChecker.waitForStable(300, Math.min(remaining, 3000));
    }

    this.logger.log('[NavigationCompleteDetector] Page stable');
  }

  /**
   * Stop monitoring
   */
  stop() {
    if (!this.isStarted) return;

    this.logger.log('[NavigationCompleteDetector] Stopping navigation monitoring');
    this.isStarted = false;

    if (this.popstateHandler) {
      window.removeEventListener('popstate', this.popstateHandler);
      this.popstateHandler = null;
    }

    // Restore original history methods
    if (this.originalPushState) {
      history.pushState = this.originalPushState;
      this.originalPushState = null;
    }
    if (this.originalReplaceState) {
      history.replaceState = this.originalReplaceState;
      this.originalReplaceState = null;
    }

    this.urlChangeCallback = null;
  }
}

/**
 * BatchedActionExecutor - Executes multiple UI actions in rapid sequence
 * Used for fast navigation without LLM round-trips between steps
 * 
 * Example: Settings → Cloud Accounts → Onboard Account in ~800ms
 */
export class BatchedActionExecutor {
  private readonly logger: Logger;
  private overlay: OverlaySystem;
  private loadingDetector: LoadingDetector;
  private domIntrospector: DOMIntrospector | null = null;
  private isExecuting: boolean = false;

  constructor(overlay: OverlaySystem, _actionExecutor: ActionExecutor, logger: Logger = defaultLogger) {
    this.logger = logger;
    this.overlay = overlay;
    // Note: We use direct click simulation for speed instead of actionExecutor
    this.loadingDetector = new LoadingDetector(logger);
  }

  /**
   * Set DOM introspector for capturing snapshots after each step
   */
  setDOMIntrospector(introspector: DOMIntrospector) {
    this.domIntrospector = introspector;
    this.logger.log('[BatchedActionExecutor] DOMIntrospector set for step captures');
  }

  /**
   * Check if currently executing a batch
   */
  get isBusy(): boolean {
    return this.isExecuting;
  }

  /**
   * Execute a batch of navigation steps quickly
   * @returns Object with success status and completedSteps count
   */
  async executeBatch(request: BatchedActionRequest): Promise<{ success: boolean; completedSteps: number }> {
    if (this.isExecuting) {
      this.logger.warn('[BatchedActionExecutor] Already executing a batch');
      return { success: false, completedSteps: 0 };
    }

    this.isExecuting = true;
    let completedSteps = 0;
    this.logger.log(`[BatchedActionExecutor] ⚡ Starting fast batch with ${request.steps.length} steps`);

    try {
      for (let i = 0; i < request.steps.length; i++) {
        const step = request.steps[i];
        this.logger.log(`[BatchedActionExecutor] Step ${i + 1}/${request.steps.length}: ${step.action}`);

        const success = await this.executeStep(step);

        if (!success) {
          this.logger.error(`[BatchedActionExecutor] ❌ Step ${i + 1} failed, aborting batch (completed ${completedSteps} steps)`);
          return { success: false, completedSteps };
        }

        completedSteps++;
        // Delay between steps - allow UI to settle
        // 300ms for clicks (need time for UI reaction), 100ms for waits (already waited)
        const delayMs = step.action === 'wait' ? 100 : 300;
        await new Promise(r => setTimeout(r, delayMs));
      }

      this.logger.log('[BatchedActionExecutor] ✅ Batch completed successfully');

      if (request.completionCallback) {
        request.completionCallback();
      }

      return { success: true, completedSteps };
    } finally {
      this.isExecuting = false;
      this.overlay.clearHighlight();
      this.overlay.hideFloeCursor();
    }
  }

  private async executeStep(step: BatchStep): Promise<boolean> {
    switch (step.action) {
      case 'click':
        return await this.executeClickStep(step);

      case 'wait':
        return await this.executeWaitStep(step);

      case 'scroll':
        return await this.executeScrollStep(step);

      default:
        this.logger.warn(`[BatchedActionExecutor] Unknown action: ${step.action}`);
        return false;
    }
  }

  /**
   * Execute a click step with proper validation
   * Ensures we click the RIGHT element, not just any matching element
   */
  private async executeClickStep(step: BatchStep): Promise<boolean> {
    if (!step.target) {
      this.logger.error('[BatchedActionExecutor] Click step missing target');
      return false;
    }

    const expectedText = step.target.text?.toLowerCase().trim();
    const expectedTag = step.target.tag?.toLowerCase();

    this.logger.log('[BatchedActionExecutor] Looking for element:', {
      text: step.target.text,
      tag: step.target.tag,
      selector: step.target.selector,
      ariaLabel: step.target.ariaLabel,
      role: step.target.role,
      labelText: step.target.labelText,
      selectorFallbacks: step.target.selectorFallbacks?.length
        ? `${step.target.selectorFallbacks.length} fallback(s)`
        : undefined,
    });

    // Resolve element with retries
    let element: HTMLElement | null = null;
    const maxRetries = 3;
    const retryDelays = [300, 500, 800]; // Increasing delays

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      if (attempt > 0) {
        this.logger.log(`[BatchedActionExecutor] Retry ${attempt}/${maxRetries} in ${retryDelays[attempt - 1]}ms...`);
        await new Promise(r => setTimeout(r, retryDelays[attempt - 1]));
      }

      element = HybridTargetResolver.resolveTarget(step.target);

      if (element) {
        // CRITICAL: Validate the found element matches our expectations
        const foundText = element.textContent?.trim().toLowerCase() || '';
        const foundTag = element.tagName.toLowerCase();

        // Text validation - must contain expected text OR be a truncated version of it
        if (expectedText && !foundText.includes(expectedText)) {
          // Check if the element text is a CSS-truncated version of the expected text.
          // CSS text-overflow:ellipsis causes textContent to be shorter than the plan's full text.
          // Accept if: (a) expected text starts with found text, AND
          //            (b) found text is long enough to be unambiguous (not just a shared prefix)
          const isTruncatedMatch =
            foundText.length >= 15 && // Must have enough chars to be meaningful
            expectedText.startsWith(foundText) &&
            foundText.length / expectedText.length >= 0.3; // At least 30% of expected text

          if (isTruncatedMatch) {
            // Ambiguity check: ensure no OTHER element on the page shares this same prefix
            // This guards against two different items that start with the same text
            const candidatesWithSamePrefix = Array.from(document.querySelectorAll(expectedTag || '*'))
              .filter(el => {
                if (el === element) return false; // Skip the element we already found
                const txt = el.textContent?.trim().toLowerCase() || '';
                // Another element is ambiguous if it also starts with the same found text
                // but is a different element (could be a different option with the same prefix)
                return txt.length >= foundText.length &&
                  txt.startsWith(foundText) &&
                  txt !== foundText; // Different full text means different element
              });

            if (candidatesWithSamePrefix.length > 0) {
              this.logger.warn(
                `[BatchedActionExecutor] ⚠️ Truncated text match is ambiguous - ${candidatesWithSamePrefix.length} other element(s) share the same prefix "${foundText.slice(0, 40)}..."`,
                candidatesWithSamePrefix.map(el => `${el.tagName}: "${el.textContent?.trim().slice(0, 60)}"`)
              );
              element = null; // Ambiguous - reject and retry
              continue;
            }

            this.logger.log(
              `[BatchedActionExecutor] ✓ Accepting truncated text match (${foundText.length}/${expectedText.length} chars, ${Math.round(foundText.length / expectedText.length * 100)}% overlap)`
            );
          } else {
            this.logger.warn(`[BatchedActionExecutor] ⚠️ Found element text "${foundText.slice(0, 50)}" doesn't match expected "${expectedText}"`);
            element = null; // Reject this element, try again
            continue;
          }
        }

        // Tag validation if specified (warn but don't reject - container elements may differ)
        if (expectedTag && foundTag !== expectedTag) {
          this.logger.log(`[BatchedActionExecutor] Note: Found ${foundTag} instead of expected ${expectedTag} (acceptable if it contains the text)`);
        }

        // Element is valid
        break;
      }
    }

    if (!element) {
      this.logger.error('[BatchedActionExecutor] ❌ Could not find matching element after retries:', step.target);
      // Log available elements that might match partially
      if (expectedText) {
        const allWithText = Array.from(document.querySelectorAll('*'))
          .filter(el => el.textContent?.toLowerCase().includes(expectedText))
          .slice(0, 5)
          .map(el => `${el.tagName.toLowerCase()}: "${el.textContent?.trim().slice(0, 50)}"`);
        if (allWithText.length > 0) {
          this.logger.log('[BatchedActionExecutor] Similar elements found:', allWithText);
        }
      }
      return false;
    }

    this.logger.log('[BatchedActionExecutor] ✓ Found element:', element.tagName, element.textContent?.slice(0, 30));

    // Scroll into view with time for layout to settle
    element.scrollIntoView({ behavior: 'auto', block: 'center' });
    await new Promise(r => setTimeout(r, 150));

    // Brief highlight
    this.overlay.highlightResolvedElement(element, {
      color: 'rgba(124, 58, 237, 0.4)',
      padding: 4
    });
    await new Promise(r => setTimeout(r, 100));

    // Direct click
    this.simulateClick(element);

    // Allow UI to react to click
    await new Promise(r => setTimeout(r, 100));
    this.overlay.clearHighlight();

    // CRITICAL: Capture DOM after each click step
    // This ensures server has fresh state for next step resolution
    if (this.domIntrospector) {
      await new Promise(r => setTimeout(r, 100)); // Extra settle time for React
      // captureAndSendImmediate, NOT scheduleCapture: this is a barrier, not an
      // event-driven trigger. The next batch step resolves its target against
      // this snapshot, so it has to have been taken before this method returns —
      // a coalesced capture would still be pending ~250ms later while the next
      // step targeted stale DOM. This is the one site where paying for an
      // uncoalesced walk is the point.
      this.domIntrospector.captureAndSendImmediate('post-batch-step-click');
    }

    return true;
  }

  /**
   * Simulate a click on an element using human-like click behavior.
   * Uses elementFromPoint to dispatch events to whatever is actually at the coordinates.
   *
   * Wraps humanLikeClick in a try/catch so exceptions from modal overlays or
   * other blocking elements don't escape and abort the entire executeBatch run.
   * On failure we throw a controlled error that executeBatch can record as a
   * failed step with full context.
   */
  private simulateClick(element: HTMLElement): void {
    try {
      // Use humanLikeClick for more reliable clicking
      // includeMouseEnter helps with React/Vue/Angular compatibility
      const actualTarget = humanLikeClick(element, {
        focus: true,
        includePointerEvents: true,
        includeMouseEnter: true,  // BatchedActionExecutor benefits from mouseenter/mouseover
        nativeClickFallback: false
      });

      // For links, log the navigation
      if (actualTarget.tagName === 'A' && (actualTarget as HTMLAnchorElement).href) {
        this.logger.log('[BatchedActionExecutor] Clicked link:', (actualTarget as HTMLAnchorElement).href);
      }
    } catch (err) {
      const tagInfo = element.tagName?.toLowerCase() || 'unknown';
      const hrefInfo = (element as HTMLAnchorElement).href || '';
      const textSnippet = element.textContent?.trim().slice(0, 50) || '';
      this.logger.error(
        `[BatchedActionExecutor] simulateClick failed on <${tagInfo}>` +
        `${hrefInfo ? ` href="${hrefInfo}"` : ''} "${textSnippet}":`,
        err
      );
      // Re-throw a controlled error so executeBatch records this as a failed
      // step instead of silently succeeding. Include the original error for
      // full context.
      const message = err instanceof Error ? err.message : String(err);
      const stepError = new Error(
        `[BatchedActionExecutor] Click step failed on <${tagInfo}> "${textSnippet}": ${message}`
      );
      (stepError as any).cause = err;
      throw stepError;
    }
  }

  private async executeWaitStep(step: BatchStep): Promise<boolean> {
    const condition = step.condition || 'page_stable';
    const timeout = step.waitMs || 5000; // Increased default timeout

    switch (condition) {
      case 'page_stable':
        await this.loadingDetector.waitForLoadingComplete(timeout);
        await new Promise(r => setTimeout(r, 400)); // More time for UI to settle
        return true;

      case 'loading_complete':
        return await this.loadingDetector.waitForLoadingComplete(timeout);

      case 'element_visible':
        if (!step.target) return true;
        return await this.waitForElement(step.target, timeout);

      default:
        await new Promise(r => setTimeout(r, step.waitMs || 500));
        return true;
    }
  }

  private async executeScrollStep(step: BatchStep): Promise<boolean> {
    if (!step.target) {
      this.logger.warn('[BatchedActionExecutor] Scroll step has no target');
      return false;
    }

    const element = HybridTargetResolver.resolveTarget(step.target);
    if (!element) {
      this.logger.warn('[BatchedActionExecutor] Scroll target not found:', step.target);
      // For scroll, we can continue even if element not found (non-critical)
      return true;
    }

    element.scrollIntoView({ behavior: 'auto', block: 'center' });
    await new Promise(r => setTimeout(r, 100));
    return true;
  }

  private async waitForElement(target: HybridTarget, timeout: number): Promise<boolean> {
    const start = Date.now();

    while (Date.now() - start < timeout) {
      const element = HybridTargetResolver.resolveTarget(target);
      if (element) return true;
      await new Promise(r => setTimeout(r, 100));
    }

    return false;
  }
}
